import { useEffect, useRef, useState, useCallback } from "react";
import { BACKEND_URL } from "@/lib/api";

/**
 * Subscribe to backend WebSocket events (admin realtime).
 * onEvent({ event, payload })
 */
export function useAdminSocket(onEvent) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const handlerRef = useRef(onEvent);
  const stopRef = useRef(false);
  handlerRef.current = onEvent;

  const connect = useCallback(() => {
    const token = localStorage.getItem("resto_token");
    if (!token) return;
    const rawBaseUrl = BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const baseUrl = rawBaseUrl.replace(/\/+$/, "");
    const wsUrl = (baseUrl ? baseUrl.replace(/^http/, "ws") : "") + "/api/ws?token=" + encodeURIComponent(token);
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        if (ev.data === "pong") return;
        try {
          const msg = JSON.parse(ev.data);
          handlerRef.current?.(msg);
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (stopRef.current) return;
        // Exponential backoff, max 15s
        const delay = Math.min(1000 * Math.pow(2, retryRef.current), 15000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    stopRef.current = false;
    connect();
    // Heartbeat every 25s
    const iv = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try { wsRef.current.send("ping"); } catch {}
      }
    }, 25000);
    return () => {
      stopRef.current = true;
      clearInterval(iv);
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  return { connected };
}
